import { getOrgPermissions } from 'services/role';

export type IPermissionObject = {
  name: string;
  actions: { [key: string]: boolean };
};

export const getModulePermissions = (permissions: string[]) => {
  const formattedPermissions: Record<string, string[]> = {};

  permissions.forEach((permission) => {
    const [action, entity] = permission.split(':');
    if (!formattedPermissions[entity]) {
      formattedPermissions[entity] = [];
    }
    formattedPermissions[entity].push(action);
  });

  return formattedPermissions;
};

export const getPermissionsObject = (permissions: string[]) => {
  const permissionsObject: Array<IPermissionObject> = [];

  permissions.forEach((permission) => {
    const [action, permissionName] = permission.split(':');
    const existingPermissionObject = permissionsObject.find(({ name }) => name === permissionName);
    if (!existingPermissionObject) {
      const newPermissions = {
        name: permissionName,
        actions: {
          [action]: true,
        },
      };

      permissionsObject.push(newPermissions);
    } else {
      existingPermissionObject.actions[action] = true;
    }
  });
  return permissionsObject;
};

export const populateRequiredPermissions = async (permissions: string[]) => {
  const permissionsObject = getPermissionsObject(permissions);
  const populatedPermissions: Array<IPermissionObject> = [];
  const orgPermissionList = await getOrgPermissions();
  const REQUIRED_PERMISSIONS = generateRequiredPermissions(orgPermissionList, permissionsObject);

  REQUIRED_PERMISSIONS.forEach((permission) => {
    const modifiedPermissions = { ...permission };

    const existedPermission = permissionsObject.find((p) => p.name === permission.name);
    if (existedPermission) {
      modifiedPermissions.actions = { ...permission.actions, ...existedPermission.actions };
      populatedPermissions.push(modifiedPermissions);
    } else {
      populatedPermissions.push(permission);
    }
  });

  permissionsObject.forEach((permission) => {
    const requiredPermissionNames = REQUIRED_PERMISSIONS.map((p) => p.name);

    const isExist = requiredPermissionNames.includes(permission.name);
    if (!isExist) {
      populatedPermissions.push(permission);
    }
  });

  return populatedPermissions.sort((a: any, b: any) => {
    const nameA = a.name.toUpperCase(); // ignore upper and lowercase
    const nameB = b.name.toUpperCase(); // ignore upper and lowercase
    if (nameA < nameB) {
      return -1;
    }
    if (nameA > nameB) {
      return 1;
    }
    // names must be equal
    return 0;
  });
};

/**
 * Generates a consolidated list of permissions based on the organization's permission list
 * and an existing permissions object.
 *
 * @param orgPermissionList - Array of permissions defined by the organization. Each item includes:
 *                            - description: A textual description of the permission.
 *                            - value: A string in the format "action:resource".
 * @param permissionsObject - Array of existing permissions, where each item includes:
 *                            - name: The resource name.
 *                            - actions: A map of actions to boolean values.
 * @returns Array of consolidated permission objects with their respective actions.
 */
export const generateRequiredPermissions = (
  orgPermissionList: { description: string; value: string }[],
  permissionsObject: IPermissionObject[]
): IPermissionObject[] => {
  // Default actions (CRUD) for resources with no special configurations
  const defaultActions: { [key: string]: boolean } = {
    create: false,
    read: false,
    update: false,
    delete: false,
    view: false,
  };

  // Custom actions for specific resources with unique permissions
  const customActions: { [key: string]: { [action: string]: boolean } } = {
    sales_price: { decrease: false, change: false },
    purchase_price: { read: false },
    pos: { read: false },
    'activity-history': { read: false },
    'restaurant-pos': { read: false },
    'restaurant-sales': { read: false },
    kitchen: { read: false },
    staff: { read: false },
    dashboard: { read: false },
  };

  // Object to store a consolidated map of resource -> actions
  const permissionsMap: { [key: string]: { [action: string]: boolean } } = {};

  // Step 1: Parse orgPermissionList into the permissionsMap
  orgPermissionList.forEach(({ value }) => {
    const [action, resource] = value.split(':'); // Split the value into action and resource
    if (!permissionsMap[resource]) {
      // Initialize the resource's actions with custom or default actions
      permissionsMap[resource] = { ...(customActions[resource] || defaultActions) };
    }
    // Set the action to false (default state from orgPermissionList)
    permissionsMap[resource][action] = false;
  });

  // Step 2: Merge existing permissionsObject into permissionsMap
  permissionsObject.forEach(({ name, actions }) => {
    if (!permissionsMap[name]) {
      // If the resource doesn't exist in permissionsMap, initialize it
      permissionsMap[name] = { ...(customActions[name] || defaultActions) };
    }
    // Merge existing actions into the resource's actions
    permissionsMap[name] = { ...permissionsMap[name], ...actions };
  });

  // Step 3: Convert permissionsMap into an array of permission objects
  return Object.keys(permissionsMap).map((resource) => ({
    name: resource, // Resource name
    actions: permissionsMap[resource], // Consolidated actions for the resource
  }));
};
